import { Fade } from "@mui/material";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Provider } from "react-redux";
import { AvatarProvider } from "../../context/AvatarContext";
import { EventProvider } from "../../context/EventContext";
import { LoginModalProvider } from "../../context/LoginModalContext";
import { useLoginStatus } from "../../context/LoginStatusContext";
import { NoticeProvider } from "../../context/NoticeContext";
import RouteGuard from "../common/RouteGuard";
import store from "../../redux/store";
import MainRouter from "../../routes/MainRouter";
import { Images } from "../../assets";
import Notice from "../common/Notice";
import ScrollToTopButton from "../common/ScrollToTopButton";
import DrawerMain from "./Drawer/DrawerMain";
import Footer from "./Footer";
import HamburgerMenu from "./Header/HamburgerMenu";
import Header from "./Header/Header";

const Layout = () => {
  return (
    <>
      <LoginModalProvider>
        <AvatarProvider>
          <EventProvider>
            <NoticeProvider>
              <Provider store={store}>
                <Header />
                <Contents />
                <Notice />
              </Provider>
            </NoticeProvider>
          </EventProvider>
        </AvatarProvider>
      </LoginModalProvider>
    </>
  );
};
export default Layout;

// 固定表示するページのURLパターン
const FIXED_PAGES = ["/chat/", "/video-generator", "/models"];

const Contents = () => {
  const { isLoggedIn, checkLoginStatus } = useLoginStatus();
  const location = useLocation();

  // 現在のURLが固定表示すべきパターンに一致するか確認
  const shouldBeFixed = useMemo(() => {
    return FIXED_PAGES.some((pattern) => location.pathname.includes(pattern));
  }, [location.pathname]);

  // ログイン状態の初期化
  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      try {
        await checkLoginStatus();
        // コンポーネントがアンマウントされていたら処理を中止
        if (!mounted) return;
      } catch (error) {
        console.error("Failed to initialize:", error);
      }
    };

    initialize();

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <>
      <>
        {isLoggedIn ? (
          <Fade in={true} timeout={1500}>
            <Box flexGrow={1}>
              <DrawerMain />
              <StyledDiv id={"main"} $isFixed={shouldBeFixed}>
                <RouteGuard>
                  <MainRouter />
                </RouteGuard>
                <Box sx={{ display: { xs: "none", sm: "flex" } }}>
                  <ScrollToTopButton />
                </Box>
              </StyledDiv>
            </Box>
          </Fade>
        ) : (
          <Fade in={true} timeout={1500}>
            <div>
              <Box sx={{ display: { xs: "block", lg: "none" } }}>
                <HamburgerMenu />
              </Box>
              <StyledDiv id={"main"} $isFixed={shouldBeFixed}>
                <RouteGuard>
                  <MainRouter />
                </RouteGuard>
                <ScrollToTopButton />
              </StyledDiv>
            </div>
          </Fade>
        )}
      </>
      <Box
        component="footer"
        sx={{
          width: "100%",
        }}
      >
        <Footer />
      </Box>
    </>
  );
};

// $isFixedプロパティを型定義
interface StyledDivProps {
  $isFixed: boolean;
}

const StyledDiv = styled("div")<StyledDivProps>(({ theme, $isFixed }) => ({
  transition: theme.transitions.create(["margin", "transform", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: 0,
  width: "100vw",
  backgroundImage: `url(${Images.common.bg01})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  minHeight: "100vh",
  ...($isFixed && {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflowY: "auto",
  }),
}));
